import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  ArrowSquareOut,
  EyeSlash,
  Flag,
  MagnifyingGlass,
  Star,
  ThumbsUp,
} from "@phosphor-icons/react";
import {
  Avatar,
  AvatarFallback,
  AvatarImage,
  DataRow,
  DataTable,
  IconButton,
  LoadingOverlay,
  PaginationFooter,
  Toggle,
  buildPageList,
  initialsForMember,
} from "@breakingthelines/design-system";

import {
  flagContent,
  listContent,
  listContentFlags,
  reviewContentFlag,
  type ContentFlagItem,
} from "@/lib/admin-api";

import styles from "./content-list.module.css";

const DEFAULT_PAGE_SIZE = 10;
const PER_PAGE_OPTIONS = [10, 20, 50];

const TABLE_COLUMNS =
  "minmax(220px, 2fr) minmax(150px, 1.1fr) minmax(90px, 0.8fr) minmax(90px, 0.8fr) minmax(110px, 0.9fr) minmax(80px, 0.7fr) minmax(120px, 0.9fr) minmax(100px, 0.8fr) minmax(70px, 0.6fr)";

function useDebouncedValue<T>(value: T, delay = 300) {
  const [debounced, setDebounced] = useState(value);

  useEffect(() => {
    const handle = window.setTimeout(() => setDebounced(value), delay);
    return () => window.clearTimeout(handle);
  }, [value, delay]);

  return debounced;
}

function formatType(type?: string) {
  if (!type) return "—";
  const normalized = type.replace("CONTENT_TYPE_", "").replace(/_/g, " ").toLowerCase();
  return normalized.charAt(0).toUpperCase() + normalized.slice(1);
}

function formatStatus(status?: string) {
  if (!status) return "—";
  const normalized = status.replace("CONTENT_STATUS_", "").replace(/_/g, " ").toLowerCase();
  return normalized.charAt(0).toUpperCase() + normalized.slice(1);
}

function formatEngagement(item: {
  viewCount?: number;
  likeCount?: number;
  thoughtCount?: number;
  bookmarkCount?: number;
}) {
  const views = item.viewCount ?? 0;
  const likes = item.likeCount ?? 0;
  const thoughts = item.thoughtCount ?? 0;
  const bookmarks = item.bookmarkCount ?? 0;
  const total = views + likes + thoughts + bookmarks;
  return total > 0 ? total.toLocaleString() : "0";
}

export function ContentListPage() {
  const [searchInput, setSearchInput] = useState("");
  const debouncedQuery = useDebouncedValue(searchInput.trim());
  const [query, setQuery] = useState("");
  const [pageIndex, setPageIndex] = useState(1);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  const [items, setItems] = useState<Array<ReturnType<typeof mapContent>>>([]);
  const [pageInfo, setPageInfo] = useState<{
    totalCount?: number;
    hasNextPage?: boolean;
    hasPreviousPage?: boolean;
  } | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [flagsByContent, setFlagsByContent] = useState<Map<string, ContentFlagItem[]>>(new Map());
  const [actionLoading, setActionLoading] = useState<string | null>(null);

  useEffect(() => {
    setQuery(debouncedQuery);
    setPageIndex(1);
  }, [debouncedQuery]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);

    // The flag list is a DECORATION — it only adds "pending flag" badges to rows.
    // Under Promise.all its rejection took the whole listing down with it, so a
    // failing flags call rendered an error and an empty table even though the
    // content itself had loaded fine. That is exactly what a moderator hits when
    // they most need this page: the flags RPC 401s on an expired token, and the
    // content they came to moderate becomes unreachable and unsearchable.
    //
    // allSettled keeps the two independent: content failing is a real error,
    // flags failing costs the badges and nothing else.
    Promise.allSettled([
      listContent({ query, page: pageIndex, pageSize }),
      listContentFlags({ status: "FLAG_STATUS_PENDING", pageSize: 100 }),
    ])
      .then(([contentResult, flagsResult]) => {
        if (cancelled) return;

        if (contentResult.status === "rejected") {
          const reason = contentResult.reason as Error | undefined;
          setError(reason?.message ?? "Failed to load content");
          setItems([]);
          setPageInfo(null);
          setFlagsByContent(new Map());
          return;
        }

        const contentData = contentResult.value;
        setItems((contentData.items ?? []).map(mapContent));
        setPageInfo(contentData.pageInfo ?? null);

        const map = new Map<string, ContentFlagItem[]>();
        if (flagsResult.status === "fulfilled") {
          for (const flag of flagsResult.value.items ?? []) {
            const existing = map.get(flag.contentId) ?? [];
            existing.push(flag);
            map.set(flag.contentId, existing);
          }
        }
        setFlagsByContent(map);
      })
      .catch((err: Error) => {
        // allSettled never rejects, so this only fires if the handler above
        // throws. Kept so such a bug surfaces instead of leaving a stuck spinner.
        if (cancelled) return;
        setError(err.message ?? "Failed to load content");
        setItems([]);
        setPageInfo(null);
      })
      .finally(() => {
        if (!cancelled) {
          setLoading(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [query, pageIndex, pageSize]);

  const handleReviewAll = (contentId: string, status: string) => {
    const flags = flagsByContent.get(contentId);
    if (!flags || flags.length === 0) return;

    setActionLoading(contentId);
    Promise.all(flags.map((f) => reviewContentFlag({ flagId: f.id, status })))
      .then(() => {
        setFlagsByContent((prev) => {
          const next = new Map(prev);
          next.delete(contentId);
          return next;
        });
      })
      .catch((err: Error) => {
        setError(err.message ?? "Action failed");
      })
      .finally(() => {
        setActionLoading(null);
      });
  };

  const handleFlag = (contentId: string) => {
    setActionLoading(contentId);
    flagContent({ contentId, reason: "FLAG_REASON_OTHER", details: "Flagged by admin" })
      .then((data) => {
        setFlagsByContent((prev) => {
          const next = new Map(prev);
          next.set(contentId, [...(prev.get(contentId) ?? []), data.flag]);
          return next;
        });
      })
      .catch((err: Error) => {
        setError(err.message ?? "Failed to flag content");
      })
      .finally(() => {
        setActionLoading(null);
      });
  };

  const totalLabel = useMemo(() => {
    if (pageInfo?.totalCount && pageInfo.totalCount > 0) {
      return `Total Content: ${pageInfo.totalCount}`;
    }
    return `Total Content: ${items.length}`;
  }, [pageInfo, items.length]);

  const totalPages = useMemo(() => {
    if (pageInfo?.totalCount && pageInfo.totalCount > 0) {
      return Math.max(1, Math.ceil(pageInfo.totalCount / pageSize));
    }
    return pageIndex;
  }, [pageInfo?.totalCount, pageIndex, pageSize]);

  const pages = useMemo(() => buildPageList(totalPages, pageIndex), [totalPages, pageIndex]);
  const activePage = Math.min(pageIndex, totalPages);
  const hasPrevious = pageIndex > 1;
  const hasNext = pageInfo?.hasNextPage ?? (items.length === pageSize && items.length > 0);

  return (
    <div className={styles.page}>
      <h1 className={styles.title}>Content</h1>

      <div className={styles.toolbar}>
        <div className={styles.search}>
          <MagnifyingGlass className={styles.searchIcon} aria-hidden />
          <input
            className={styles.searchInput}
            placeholder="Search"
            value={searchInput}
            onChange={(event) => setSearchInput(event.currentTarget.value)}
          />
        </div>
      </div>

      <div className={styles.tableSection}>
        {loading ? <LoadingOverlay label="Loading content..." /> : null}
        <DataTable
          columns={TABLE_COLUMNS}
          header={[
            "Title",
            "Creator",
            "Type",
            "Status",
            "Engagement",
            "Flags",
            "Featured",
            "Actions",
            "View",
          ]}
          className={styles.contentTable}
          headerClassName={styles.tableHeader}
          bodyClassName={styles.rows}
        >
          {items.map((row, index) => (
            <DataRow key={`${row.id}-${index}`} className={styles.contentRow}>
              <span className={styles.cellText} data-label="Title">
                {row.title}
              </span>
              <div className={styles.creator}>
                <Avatar size="sm" className={`size-5 ${styles.creatorAvatar}`} aria-hidden="true">
                  {row.creatorAvatar ? <AvatarImage src={row.creatorAvatar} alt="" /> : null}
                  <AvatarFallback className="text-[10px]">
                    {initialsForMember(row.creatorInitials)}
                  </AvatarFallback>
                </Avatar>
                {row.creatorHandle ? (
                  <Link to={`/users/${row.creatorHandle}`} className={styles.creatorLink}>
                    {row.creatorLabel}
                  </Link>
                ) : (
                  <span className={styles.cellText}>{row.creatorLabel}</span>
                )}
              </div>
              <span className={styles.cellText} data-label="Type">
                {row.type}
              </span>
              <span className={styles.statusBadge}>{row.status}</span>
              <span className={styles.cellText} data-label="Engagement">
                {row.engagement}
              </span>
              <span className={styles.cellText} data-label="Flags">
                {flagsByContent.get(row.id)?.length ?? 0}
              </span>
              <div className={styles.featuredCell}>
                <span className={styles.cellText}>Featured</span>
                {/* The design-system has no switch. Its Toggle is a pressed icon
                    button, so the control carries an icon rather than rendering
                    as an empty box. */}
                <Toggle aria-label="Toggle featured" size="sm">
                  <Star size={14} />
                </Toggle>
              </div>
              <div className={styles.actionButtons}>
                <IconButton
                  aria-label="Flag content"
                  variant="ghost"
                  size="md"
                  onClick={actionLoading === row.id ? undefined : () => handleFlag(row.id)}
                >
                  <Flag size={14} />
                </IconButton>
                {(flagsByContent.get(row.id)?.length ?? 0) > 0 ? (
                  <>
                    <IconButton
                      aria-label="Mark flags reviewed"
                      variant="ghost"
                      size="md"
                      onClick={
                        actionLoading === row.id
                          ? undefined
                          : () => handleReviewAll(row.id, "FLAG_STATUS_REVIEWED")
                      }
                    >
                      <EyeSlash size={14} />
                    </IconButton>
                    <IconButton
                      aria-label="Dismiss flags"
                      variant="ghost"
                      size="md"
                      onClick={
                        actionLoading === row.id
                          ? undefined
                          : () => handleReviewAll(row.id, "FLAG_STATUS_DISMISSED")
                      }
                    >
                      <ThumbsUp size={14} />
                    </IconButton>
                  </>
                ) : null}
              </div>
              <Link to={`/content/${row.id}`} className={styles.viewButton}>
                <span>View</span>
                <ArrowSquareOut size={14} />
              </Link>
            </DataRow>
          ))}
        </DataTable>
        {!loading && error ? <div className={styles.emptyState}>{error}</div> : null}
        {!loading && !error && items.length === 0 ? (
          <div className={styles.emptyState}>No content found.</div>
        ) : null}
      </div>

      <PaginationFooter
        totalLabel={totalLabel}
        pages={pages}
        activePage={activePage}
        hasPrevious={hasPrevious}
        hasNext={hasNext}
        onPrevious={() => setPageIndex((page) => Math.max(1, page - 1))}
        onNext={() => setPageIndex((page) => page + 1)}
        perPage={pageSize}
        perPageOptions={PER_PAGE_OPTIONS}
        onPerPageChange={(option) => {
          setPageSize(option);
          setPageIndex(1);
        }}
        onPageChange={(page) => {
          if (page === activePage) return;
          setPageIndex(page);
        }}
        className={styles.tableFooter}
      />
    </div>
  );
}

function mapContent(item: {
  id?: string;
  title?: string;
  type?: string;
  status?: string;
  creator?: { username?: string; displayName?: string; avatarUrl?: string };
  viewCount?: number;
  likeCount?: number;
  thoughtCount?: number;
  bookmarkCount?: number;
}) {
  const creatorName = item.creator?.displayName || item.creator?.username || "";
  const creatorHandle = item.creator?.username || "";
  return {
    id: item.id ?? item.title ?? "unknown",
    title: item.title ?? "Untitled",
    creatorHandle,
    creatorLabel: creatorHandle ? `@${creatorHandle}` : "@unknown",
    creatorAvatar: item.creator?.avatarUrl,
    creatorInitials: creatorName,
    type: formatType(item.type),
    status: formatStatus(item.status),
    engagement: formatEngagement(item),
  };
}
